import { getDeviceInfo, getDeviceFingerprint } from './deviceFingerprint';

/**
 * Context-Aware Authentication System
 * Provides intelligent security policies based on device context, location, and usage patterns
 */

export interface DeviceContext {
  type: 'work' | 'personal' | 'public' | 'unknown';
  location: 'home' | 'office' | 'travel' | 'unknown';
  timeOfDay: 'work_hours' | 'off_hours' | 'unknown';
  riskLevel: 'low' | 'medium' | 'high';
  trustScore: number; // 0-100
}

export interface AuthPolicy {
  requireTwoFactor: boolean;
  trustDuration: number; // days
  maxSessionTime: number; // hours
  requireLocationVerification: boolean;
  allowBiometricFallback: boolean;
  securityLevel: 'basic' | 'standard' | 'enhanced';
}

/**
 * Analyze device context to determine appropriate security policies
 */
export const analyzeDeviceContext = (): DeviceContext => {
  try {
    const deviceInfo = getDeviceInfo();
    const now = new Date();
    const hour = now.getHours();

    // Determine device type based on user agent and patterns
    let deviceType: DeviceContext['type'] = 'unknown';
    const ua = (deviceInfo.userAgent as string).toLowerCase();

    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      deviceType = 'personal'; // Mobile devices are typically personal
    } else if (ua.includes('windows') && hour >= 9 && hour <= 17) {
      deviceType = 'work'; // Windows during work hours likely work device
    } else if (ua.includes('mac') && hour >= 9 && hour <= 17) {
      deviceType = 'work'; // Mac during work hours likely work device
    } else {
      deviceType = 'personal'; // Default to personal
    }

    // Determine location (simplified - in production would use IP geolocation)
    let location: DeviceContext['location'] = 'unknown';
    // This would be enhanced with actual geolocation services
    location = 'unknown'; // Placeholder

    // Determine time of day
    let timeOfDay: DeviceContext['timeOfDay'] = 'unknown';
    if (hour >= 6 && hour <= 18) {
      timeOfDay = 'work_hours';
    } else {
      timeOfDay = 'off_hours';
    }

    // Calculate risk level
    let riskLevel: DeviceContext['riskLevel'] = 'medium';
    let trustScore = 50;

    // Lower risk for known devices during work hours
    if (deviceType === 'work' && timeOfDay === 'work_hours') {
      riskLevel = 'low';
      trustScore = 85;
    } else if (deviceType === 'personal' && (location as DeviceContext['location']) === 'home') {
      riskLevel = 'low';
      trustScore = 80;
    }

    // Adjust for device capabilities
    if (deviceInfo.cookiesEnabled && deviceInfo.deviceMemory && deviceInfo.deviceMemory !== 'unknown') {
      trustScore += 10; // Modern device with capabilities
    }

    return {
      type: deviceType,
      location,
      timeOfDay,
      riskLevel,
      trustScore: Math.min(100, Math.max(0, trustScore))
    };

  } catch (error) {
    console.warn('Failed to analyze device context:', error);
    return {
      type: 'unknown',
      location: 'unknown',
      timeOfDay: 'unknown',
      riskLevel: 'medium',
      trustScore: 50
    };
  }
};

/**
 * Get authentication policy based on device context
 */
export const getAuthPolicy = (context: DeviceContext): AuthPolicy => {
  const basePolicy: AuthPolicy = {
    requireTwoFactor: true,
    trustDuration: 7, // days
    maxSessionTime: 8, // hours
    requireLocationVerification: false,
    allowBiometricFallback: false,
    securityLevel: 'standard'
  };

  // Adjust policy based on context
  switch (context.type) {
    case 'work':
      if (context.timeOfDay === 'work_hours') {
        return {
          ...basePolicy,
          requireTwoFactor: false, // Skip 2FA for trusted work devices during work hours
          trustDuration: 30, // Longer trust period
          maxSessionTime: 12, // Longer sessions
          securityLevel: 'standard'
        };
      }
      break;

    case 'personal':
      if (context.location === 'home') {
        return {
          ...basePolicy,
          trustDuration: 14, // Moderate trust period
          maxSessionTime: 24, // Allow longer sessions at home
          allowBiometricFallback: true, // Allow biometric fallback
          securityLevel: 'basic'
        };
      }
      break;

    case 'public':
      return {
        ...basePolicy,
        requireTwoFactor: true, // Always require 2FA
        trustDuration: 1, // Very short trust period
        maxSessionTime: 2, // Short sessions
        requireLocationVerification: true, // Extra verification
        securityLevel: 'enhanced'
      };
  }

  return basePolicy;
};

/**
 * Get contextual authentication message
 */
export const getAuthContextMessage = (context: DeviceContext): string => {
  // Ultra-short messages that fit in small notification boxes without truncation
  const messages = {
    work: {
      work_hours: "Welcome back!",
      off_hours: "Evening access",
      unknown: "Recognized device"
    },
    personal: {
      home: "Welcome home",
      office: "Office access",
      travel: "Travel access",
      unknown: "Personal access"
    },
    public: {
      unknown: "Public access"
    },
    unknown: {
      unknown: "Secure access"
    }
  };

  const deviceMessages = messages[context.type];
  return (deviceMessages as any)[context.location] || deviceMessages.unknown;
};

/**
 * Check if current device context allows biometric authentication
 */
export const shouldAllowBiometricAuth = (context: DeviceContext): boolean => {
  // Allow biometrics for personal devices with low risk
  return context.type === 'personal' && context.riskLevel === 'low' && context.trustScore > 70;
};

/**
 * Get recommended security actions based on context
 */
export const getRecommendedSecurityActions = (context: DeviceContext): string[] => {
  const actions: string[] = [];

  if (context.riskLevel === 'high') {
    actions.push('Enable 2FA verification');
    actions.push('Use backup codes for additional security');
    actions.push('Review recent account activity');
  }

  if (context.type === 'public') {
    actions.push('Avoid saving passwords');
    actions.push('Clear browser data after use');
    actions.push('Use VPN for additional protection');
  }

  if (context.trustScore < 50) {
    actions.push('Verify device identity');
    actions.push('Check for unusual activity');
  }

  return actions;
};

/**
 * Context-aware authentication hook
 */
export const useContextAwareAuth = () => {
  const deviceContext = analyzeDeviceContext();
  const authPolicy = getAuthPolicy(deviceContext);
  const contextMessage = getAuthContextMessage(deviceContext);
  const allowBiometric = shouldAllowBiometricAuth(deviceContext);
  const securityActions = getRecommendedSecurityActions(deviceContext);

  return {
    deviceContext,
    authPolicy,
    contextMessage,
    allowBiometric,
    securityActions,
    deviceFingerprint: getDeviceFingerprint()
  };
};

/**
 * Enhanced login context for progressive authentication
 */
export const getProgressiveLoginContext = () => {
  const context = analyzeDeviceContext();
  const policy = getAuthPolicy(context);

  return {
    context,
    policy,
    shouldSkipTwoFactor: !policy.requireTwoFactor,
    trustDuration: policy.trustDuration,
    sessionTimeout: policy.maxSessionTime,
    requiresLocationCheck: policy.requireLocationVerification,
    biometricAvailable: shouldAllowBiometricAuth(context),
    securityLevel: policy.securityLevel,
    riskAssessment: context.riskLevel,
    trustScore: context.trustScore
  };
};

